import type { TFunction } from "i18next";
import type { BillOut } from "../../../../../api/bills";
import type { Deposit } from "../../../../../api/deposits";
import { isDepositFiscalReady } from "../../../../../lib/depositReceipt";
import { formatCents, parseMoneyToCents } from "../../../../../utils/money";

/**
 * Депозитът, закачен към ОТВОРЕНАТА сметка („Депозит" → „Използвай депозит",
 * BE `PUT /bills/{id}/deposit`) — от документа на сметката. Сумата е цялата
 * сума на депозита като низ с два знака; колко покрива се смята от текущата
 * сума на сметката (`depositCoverageOf`). Закрита сметка носи депозита, с който
 * Е платена — това е записът на повторния опит (`recordedBillDepositOf`), не тук.
 */
export interface AttachedBillDeposit {
  id: number;
  name: string;
  amount: string;
}

export const attachedDepositOf = (
  bill:
    | Pick<BillOut, "status" | "deposit_id" | "deposit_name" | "deposit_full_amount">
    | null
    | undefined,
): AttachedBillDeposit | null => {
  if (!bill || bill.status !== "open" || !bill.deposit_id) return null;
  return {
    id: bill.deposit_id,
    name: bill.deposit_name ?? "",
    amount: formatCents(parseMoneyToCents(bill.deposit_full_amount ?? 0) ?? 0),
  };
};

/** „Депозит № 8 · Боян Бочуков" — или само номерът, когато името не е
 *  известно (записът на историята носи само номера). */
export const depositTitleOf = (t: TFunction, deposit: Pick<AttachedBillDeposit, "id" | "name">) =>
  deposit.name
    ? t("closeTable.deposit.chosen", { id: deposit.id, name: deposit.name })
    : t("closeTable.deposit.number", { id: deposit.id });

/**
 * Какво отваря бутонът „Депозит" в „Опции": с двете права — избор „Нов
 * депозит" / „Използвай депозит"; с едното — направо него; без нито едно —
 * бутона го няма.
 */
export type DepositActionMode = "choice" | "load" | "use" | null;

export const depositActionModeOf = ({
  canLoad,
  canUse,
}: {
  canLoad: boolean;
  canUse: boolean;
}): DepositActionMode => {
  if (canLoad && canUse) return "choice";
  if (canLoad) return "load";
  if (canUse) return "use";
  return null;
};

/**
 * Защо „Използвай депозит" не може за избраната маса — ключ за превод под
 * `depositAction.unavailable`, или null, когато може. Депозитът се закача
 * само към отворена сметка над 0.00 (BE отказва иначе), която не се закрива
 * в момента.
 */
export type DepositUseBlock = "noBill" | "billClosed" | "zeroBill" | "closing";

export const depositUseBlockOf = (
  bill: Pick<BillOut, "status" | "total_amount"> | null | undefined,
  isClosing: boolean,
): DepositUseBlock | null => {
  if (!bill) return "noBill";
  if (bill.status !== "open") return "billClosed";
  if (isClosing) return "closing";
  if ((parseMoneyToCents(bill.total_amount) ?? 0) <= 0) return "zeroBill";
  return null;
};

/** Защо депозитът от избора не се закача към тази сметка: без касов бон или
 *  закачен към ДРУГА маса (BE отказва и двете); null — може. */
export const depositPickBlockOf = (
  deposit: Pick<Deposit, "status" | "usedBillId" | "fiscalStatus" | "fiscalReady">,
  billId: number,
): "notReady" | "otherBill" | null => {
  if (deposit.status === "attached" && deposit.usedBillId !== billId) return "otherBill";
  if (!isDepositFiscalReady(deposit)) return "notReady";
  return null;
};

/** Колко покрива депозитът от сметка — всичко в центове. */
export interface DepositCoverage {
  /** Платеното с депозита: min(депозит, сметка). */
  appliedCents: number;
  /** Остатъкът за избрания метод; 0 — депозитът покрива всичко. */
  remainderCents: number;
  /** Неползваната част на депозита — изгаря (BE не я връща никъде). */
  burnedCents: number;
}

export const depositCoverageOf = (
  depositAmount: string,
  billCents: number,
): DepositCoverage => {
  const depositCents = parseMoneyToCents(depositAmount) ?? 0;
  const appliedCents = Math.min(depositCents, Math.max(0, billCents));
  return {
    appliedCents,
    remainderCents: Math.max(0, billCents - appliedCents),
    burnedCents: Math.max(0, depositCents - appliedCents),
  };
};
